import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAnchoredPopover } from '@/components/ui/use-anchored-popover';

// =============================================================================
// operational-view-filter-menu.tsx — Menu déroulant à cases (filtres et colonnes) de la vue opérationnelle.
// =============================================================================
// Restyling 2026-09-17 : les cards « Filtres », « Famille / Sous-famille » et « Colonnes affichées » deviennent
// des menus compacts dans la barre de la card (« Niveau · tous ▾ », « Famille · tous ▾ », « Colonnes 11 / 11 ▾ »).
// Aucune dépendance ajoutée : le composant reprend le pattern popover existant (liste déroulante à cases du
// catalogue, `LevelMultiSelect`) et l'ancrage PORTALÉ de `menu-button.tsx` (position fixe calée sur le
// déclencheur) afin d'échapper au `overflow-hidden` du gabarit Panel.
//
// WCAG 2.1 AA (disclosure) : déclencheur <button aria-expanded aria-controls> ; panneau role="group" (cases) ou
// role="radiogroup" (choix unique) nommé ; chaque case porte un <label htmlFor>. Clavier : Flèche bas ouvre,
// le focus va à la 1re case ; Flèches haut/bas circulent entre les cases ; Échap ferme et rend le focus au
// déclencheur ; Tab en sortie de liste ferme et rend le focus au déclencheur ; clic hors du menu ferme.
//
// 2026-09-21 : l'ancrage portalé, le reflow et la fermeture ont été EXTRAITS dans le socle partagé
// `components/ui/use-anchored-popover.ts`, dont `FilterCombo` (liste autofiltrante) hérite aussi.
// L'API publique et le comportement de ce menu sont inchangés : seule la plomberie a déménagé.

export interface FilterPopoverProps {
  /** Libellé visible du déclencheur (ex. « Famille · tous »), porteur du nom accessible. */
  readonly triggerLabel: string;
  /** Nom accessible du panneau (ex. « Famille (N1) »). */
  readonly groupLabel: string;
  /** `radiogroup` pour un choix unique, `group` (défaut) pour des cases multiples. */
  readonly groupRole?: 'group' | 'radiogroup';
  /** Alignement du panneau sous le déclencheur. */
  readonly align?: 'start' | 'end';
  readonly children: React.ReactNode;
}

export function FilterPopover({
  triggerLabel,
  groupLabel,
  groupRole = 'group',
  align = 'start',
  children,
}: FilterPopoverProps): React.JSX.Element {
  // Plomberie (ouverture, ancrage portalé, reflow, clic extérieur) DÉLÉGUÉE au socle partagé
  // `useAnchoredPopover` : elle est désormais commune à ce menu et à `FilterCombo`. Comportement
  // strictement inchangé — à l'ouverture, le focus va à la 1re case du panneau.
  const { open, popupId, triggerRef, popupRef, popupStyle, toggle, close, openPopover } =
    useAnchoredPopover({
      align,
      onOpened: (popup) => {
        popup.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
      },
    });

  const inputs = React.useCallback(
    (): HTMLInputElement[] =>
      Array.from(
        popupRef.current?.querySelectorAll<HTMLInputElement>('input:not([disabled])') ?? [],
      ),
    [popupRef],
  );

  const onPopupKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const items = inputs();
    const index = items.indexOf(document.activeElement as HTMLInputElement);
    const target = event.target as HTMLInputElement;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        // Les boutons radio gardent leur navigation native (flèches = changement de choix).
        if (target.type !== 'checkbox' || items.length === 0) {
          break;
        }
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(index + step + items.length) % items.length]?.focus();
        break;
      }
      case 'Tab': {
        // Le panneau est portalé en fin de <body> : sortir de la liste rend le focus au déclencheur
        // (ordre de tabulation préservé). Un radiogroup n'a qu'un arrêt de tabulation.
        const leaving =
          groupRole === 'radiogroup' ||
          (event.shiftKey ? index <= 0 : index === items.length - 1);
        if (leaving) {
          event.preventDefault();
          close(true);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            openPopover();
          }
        }}
        title={triggerLabel}
        className="min-w-0 max-w-full gap-1 font-normal"
      >
        {/* Le libellé est TRONQUÉ visuellement (une valeur longue ne déborde plus de la colonne
            340 px) ; le nom accessible du bouton reste la chaîne ENTIÈRE, rappelée par le `title`. */}
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      </Button>
      {open && popupStyle !== null
        ? createPortal(
            <div
              ref={popupRef}
              id={popupId}
              role={groupRole}
              aria-label={groupLabel}
              onKeyDown={onPopupKeyDown}
              style={popupStyle}
              className="z-50 max-h-80 min-w-56 overflow-y-auto rounded-md border border-border bg-card p-1 text-card-foreground shadow-md"
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const OPTION_ROW =
  'flex items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground';

export interface CheckboxOptionProps {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/** Case à cocher nommée par son <label htmlFor> (le texte porte l'information). */
export function CheckboxOption({
  id,
  label,
  checked,
  onChange,
}: CheckboxOptionProps): React.JSX.Element {
  return (
    <div className={OPTION_ROW}>
      <input
        id={id}
        type="checkbox"
        className="size-4 rounded border-input"
        checked={checked}
        onChange={onChange}
      />
      <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
        {label}
      </Label>
    </div>
  );
}

export interface RadioOptionProps extends CheckboxOptionProps {
  /** Nom du groupe radio (un seul choix par nom). */
  readonly name: string;
}

/** Bouton radio nommé par son <label htmlFor> (choix unique, ex. Niveau). */
export function RadioOption({
  id,
  name,
  label,
  checked,
  onChange,
}: RadioOptionProps): React.JSX.Element {
  return (
    <div className={OPTION_ROW}>
      <input
        id={id}
        name={name}
        type="radio"
        className="size-4 border-input"
        checked={checked}
        onChange={onChange}
      />
      <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
        {label}
      </Label>
    </div>
  );
}
